import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import AutoLogoutComponent from '../../AutoLogoutComponent'
import axios from 'axios'
import '../../modern-theme.css'
import {
  Home,
  Users,
  GraduationCap,
  Layers,
  FileText,
  BookOpen,
  ClipboardList,
  LogOut,
  User,
  UserCircle,
  User2,
  UserCheck,
  UserCog,
  Settings,
  UserPlus,
  UserMinus,
  UserX,
  BarChart2,
  FileStack,
} from 'lucide-react'
import {
  DashboardIcon,
  SubAdminsIcon,
  ParentsIcon,
  StudentsIcon,
  BatchesIcon,
  ACTIcon,
  SATIcon,
} from './CustomIcons'

function capitalizeFirstLetter(string) {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : ''
}

// SidebarLink component for ACT and sublinks
const SidebarLink = ({
  icon,
  label,
  isActive = false,
  hasSubLinks = false,
  subLinks = [],
  onClick,
}) => {
  return (
    <div
      className={`flex w-full items-center gap-3 text-[#030303] font-medium whitespace-nowrap ${
        isActive ? 'text-[#DE3C7D] font-bold' : ''
      }`}
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        padding: '12px 28px',
      }}
    >
      <div className="self-stretch flex w-6 shrink-0 h-6 my-auto">{icon}</div>
      <div className="self-stretch my-auto">{label}</div>
      {hasSubLinks && subLinks.length > 0 && (
        <div className="relative flex w-full max-w-[172px] flex-col font-medium mt-2.5">
          {/* Gray vertical line for all sub-links */}
          <div className="bg-[rgba(246,246,246,1)] absolute z-0 flex min-h-full w-0.5 left-2.5 top-0 bottom-0" />
          {subLinks.map((subLink, index) => (
            <a
              key={index}
              href={subLink.path}
              className={`relative z-0 flex w-full items-start gap-3 whitespace-nowrap mt-1 px-10 py-2 rounded-lg max-md:px-5 ${
                subLink.isActive ? 'text-[#DE3C7D] font-bold' : 'text-[#030303]'
              }`}
              style={{ position: 'relative' }}
            >
              <div className="z-0 flex-1 shrink basis-[0%] my-auto">
                {subLink.label}
              </div>
              {/* L-connector */}
              <div
                className={`absolute z-0 flex w-[13px] h-2 left-2.5 top-2/4 -translate-y-2/4 rounded-bl-lg border-l-2 border-b-2 border-solid
                  ${
                    subLink.isActive
                      ? 'border-[color:var(--Red-Pink,#DE3C7D)]'
                      : 'border-[rgba(246,246,246,1)]'
                  }`}
              />
              {/* Pink vertical line for active sub-link */}
              {subLink.isActive && (
                <div
                  className="bg-[#DE3C7D] absolute z-0 flex w-0.5 left-2.5"
                  style={{
                    top: 0,
                    height: '100%',
                  }}
                />
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

function Leftbar() {
  const rolename = capitalizeFirstLetter(localStorage.getItem('role'))
  const first_name = localStorage.getItem('first_name')
  const last_name = localStorage.getItem('last_name')
  const userid = localStorage.getItem('userid')
  const navigate = useNavigate()
  const location = useLocation()
  const [cookies, setCookie, removeCookie] = useCookies([])
  const [pstudents, setPStudents] = useState([])
  const [curveTop, setCurveTop] = useState(90)
  const menuRefs = useRef({})

  useEffect(() => {
    if (rolename === 'Parent') {
      getParentStudents()
    }
    // eslint-disable-next-line
  }, [rolename, userid])

  useEffect(() => {
    // Find the active menu item and set the curve position
    const activePath = location.pathname
    const activeRef = menuRefs.current[activePath]
    if (activeRef && activeRef.offsetTop !== undefined) {
      // Center the curve on the menu item
      setCurveTop(activeRef.offsetTop + activeRef.offsetHeight / 2 - 32)
    }
  }, [location.pathname])

  const getParentStudents = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}get_students/${userid}`
      )
      setPStudents(response.data)
    } catch (error) {
      console.error('Error fetching tabs data:', error)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('role')
    localStorage.removeItem('first_name')
    localStorage.removeItem('last_name')
    localStorage.removeItem('userid')
    navigate('/')
  }

  // Menu definitions for each role
  const navItems = [
    { path: '/dashboard', icon: DashboardIcon, label: 'Dashboard' },
    {
      path: '/admins',
      icon: SubAdminsIcon,
      label: 'Sub Admins',
      role: 'Admin',
    },
    { path: '/parents', icon: ParentsIcon, label: 'Parents' },
    { path: '/students', icon: BatchesIcon, label: 'Students' },
    { path: '/batches', icon: StudentsIcon, label: 'Batches' },
    {
      label: 'ACT',
      icon: ACTIcon,
      path: '/tests',
      children: [
        { path: '/acttopics', icon: null, label: 'Topics' },
        { path: '/actsubtopics', icon: null, label: 'Sub Topics' },
      ],
    },
    {
      label: 'SAT',
      icon: ACTIcon,
      path: '/sattests',
      children: [
        { path: '/topics', icon: null, label: 'Topics' },
        { path: '/subtopics', icon: null, label: 'Sub Topics' },
      ],
    },
  ]
  const navItems2 = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/admins', icon: Users, label: 'Sub Admins', role: 'Admin' },
    { path: '/parents', icon: UserCircle, label: 'Parents' },
    { path: '/students', icon: GraduationCap, label: 'Students' },
    { path: '/batches', icon: BookOpen, label: 'Batches' },
  ]

  // Helper to render menu item
  const renderMenuItem = (item) => {
    if (item.label === 'ACT') {
      const isActive =
        location.pathname.startsWith('/tests') ||
        item.children.some((child) => location.pathname.startsWith(child.path))
      // Submenu should expand if on /tests, /acttopics, or /actsubtopics
      const actShouldExpand =
        location.pathname.startsWith('/tests') ||
        location.pathname.startsWith('/acttopics') ||
        location.pathname.startsWith('/actsubtopics')
      // Prepare subLinks array for SidebarLink, icon as JSX element
      const subLinks = item.children.map((child) => ({
        ...child,
        isActive: location.pathname.startsWith(child.path),
        icon: null,
      }))
      return (
        <li
          key={item.label}
          style={{ margin: 0, padding: 0, position: 'relative' }}
        >
          <div style={{ margin: '0 12px' }}>
            <SidebarLink
              icon={
                <item.icon
                  size={24}
                  strokeWidth={2}
                  style={{ color: isActive ? '#DE3C7D' : '#222' }}
                />
              }
              label={item.label}
              isActive={isActive}
              hasSubLinks={false}
              onClick={(e) => {
                e.preventDefault()
                navigate('/tests')
              }}
            />
          </div>
          {/* Sub-menu below ACT */}
          {actShouldExpand && (
            <div className="relative flex w-full max-w-[172px] flex-col font-medium mt-2.5 ml-8">
              {/* Gray vertical line for all sub-links */}
              <div className="bg-[rgba(246,246,246,1)] absolute z-0 flex min-h-full w-0.5 left-2.5 top-0 bottom-0" />
              {subLinks.map((subLink, index) => (
                <a
                  key={index}
                  href={subLink.path}
                  className={`relative z-0 flex w-full items-start gap-3 whitespace-nowrap mt-1 px-10 py-2 rounded-lg max-md:px-5 ${
                    subLink.isActive
                      ? 'text-[#DE3C7D] font-bold'
                      : 'text-[#030303]'
                  }`}
                  style={{ position: 'relative' }}
                >
                  <div className="z-0 flex-1 shrink basis-[0%] my-auto">
                    {subLink.label}
                  </div>
                  {/* L-connector */}
                  <div
                    className={`absolute z-0 flex w-[13px] h-2 left-2.5 top-2/4 -translate-y-2/4 rounded-bl-lg border-l-2 border-b-2 border-solid
                      ${
                        subLink.isActive
                          ? 'border-[color:var(--Red-Pink,#DE3C7D)]'
                          : 'border-[rgba(246,246,246,1)]'
                      }`}
                  />
                  {/* Pink vertical line for active sub-link */}
                  {subLink.isActive && (
                    <div
                      className="bg-[#DE3C7D] absolute z-0 flex w-0.5 left-2.5"
                      style={{
                        top: 0,
                        height: '100%',
                      }}
                    />
                  )}
                </a>
              ))}
            </div>
          )}
        </li>
      )
    }
    // SAT menu logic
    if (item.label === 'SAT') {
      const isActive =
        location.pathname.toLowerCase().startsWith('/sattests') ||
        item.children.some((child) =>
          location.pathname.toLowerCase().startsWith(child.path.toLowerCase())
        )
      const satShouldExpand =
        location.pathname.toLowerCase().startsWith('/sattests') ||
        location.pathname.toLowerCase() === '/topics' ||
        location.pathname.toLowerCase().startsWith('/topics/') ||
        location.pathname.toLowerCase().startsWith('/subtopics')
      const subLinks = item.children.map((child) => ({
        ...child,
        isActive: location.pathname
          .toLowerCase()
          .startsWith(child.path.toLowerCase()),
        icon: null,
      }))
      return (
        <li
          key={item.label}
          style={{ margin: 0, padding: 0, position: 'relative' }}
        >
          <div style={{ margin: '0 12px' }}>
            <SidebarLink
              icon={
                <item.icon
                  size={24}
                  strokeWidth={2}
                  style={{ color: isActive ? '#DE3C7D' : '#222' }}
                />
              }
              label={item.label}
              isActive={isActive}
              hasSubLinks={false}
              onClick={(e) => {
                e.preventDefault()
                navigate('/sattests')
              }}
            />
          </div>
          {/* Sub-menu below SAT */}
          {satShouldExpand && (
            <div className="relative flex w-full max-w-[172px] flex-col font-medium mt-2.5 ml-8">
              {/* Gray vertical line for all sub-links */}
              <div className="bg-[rgba(246,246,246,1)] absolute z-0 flex min-h-full w-0.5 left-2.5 top-0 bottom-0" />
              {subLinks.map((subLink, index) => (
                <a
                  key={index}
                  href={subLink.path}
                  className={`relative z-0 flex w-full items-start gap-3 whitespace-nowrap mt-1 px-10 py-2 rounded-lg max-md:px-5 ${
                    subLink.isActive
                      ? 'text-[#DE3C7D] font-bold'
                      : 'text-[#030303]'
                  }`}
                  style={{ position: 'relative' }}
                >
                  <div className="z-0 flex-1 shrink basis-[0%] my-auto">
                    {subLink.label}
                  </div>
                  {/* L-connector */}
                  <div
                    className={`absolute z-0 flex w-[13px] h-2 left-2.5 top-2/4 -translate-y-2/4 rounded-bl-lg border-l-2 border-b-2 border-solid
                      ${
                        subLink.isActive
                          ? 'border-[color:var(--Red-Pink,#DE3C7D)]'
                          : 'border-[rgba(246,246,246,1)]'
                      }`}
                  />
                  {/* Pink vertical line for active sub-link */}
                  {subLink.isActive && (
                    <div
                      className="bg-[#DE3C7D] absolute z-0 flex w-0.5 left-2.5"
                      style={{
                        top: 0,
                        height: '100%',
                      }}
                    />
                  )}
                </a>
              ))}
            </div>
          )}
        </li>
      )
    }
    // Default menu item
    const isActive = location.pathname === item.path
    const Icon = item.icon
    return (
      <li key={item.path} style={{ marginBottom: 8 }}>
        <a
          ref={(el) => {
            if (isActive) menuRefs.current[item.path] = el
          }}
          href={item.path}
          className={`sidebar-link${isActive ? ' active' : ''}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 28px',
            fontSize: isActive ? '17px' : '17px',
            fontWeight: isActive ? 700 : 500,
            color: isActive ? '#E6487C' : '#030303',
            fontFamily: isActive ? undefined : 'Basis Grotesque Arabic Pro',
            fontStyle: isActive ? undefined : 'normal',
            lineHeight: isActive ? undefined : '120%',
            borderRadius: 24,
            margin: '0 12px',
            textDecoration: 'none',
            background: 'transparent',
            transition: 'background 0.2s, color 0.2s',
            gap: 14,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Icon
            size={24}
            strokeWidth={2}
            style={{
              marginRight: 14,
              color: isActive ? '#E6487C' : '#222',
              minWidth: 24,
              minHeight: 24,
              display: 'inline-block',
              verticalAlign: 'middle',
              transition: 'color 0.2s',
            }}
          />
          <span
            style={{
              fontWeight: isActive ? 700 : 500,
              letterSpacing: '0.01em',
              fontSize: isActive ? '18px' : '18px',
              color: isActive ? '#E6487C' : '#030303',
              fontFamily: isActive ? undefined : 'Basis Grotesque Arabic Pro',
              fontStyle: isActive ? undefined : 'normal',
              lineHeight: isActive ? undefined : '120%',
            }}
          >
            {item.label}
          </span>
        </a>
      </li>
    )
  }

  return (
    <>
      <AutoLogoutComponent />
      <aside
        className="sidebar-modern"
        style={{
          width: 260,
          minHeight: '100vh',
          background: '#fff',
          borderRight: '1.5px solid #F2F2F2',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          padding: 0,
          zIndex: 10,
          boxShadow: '4px 0 16px 0 rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: '24px 0 24px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img
            src="/logo.png"
            alt="Brilliant Prep"
            style={{
              width: 180,
              height: 60,
              objectFit: 'contain',
              marginTop: 15,
              marginBottom: 25,
            }}
          />
          <div
            style={{
              width: '70%',
              height: 1,
              background: '#BDBDBD',
              borderRadius: 2,
            }}
          />
        </div>
        <nav style={{ flex: 1, marginTop: 18 }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(rolename === 'Student' || rolename === 'Parent') && (
              <>
                {/* Practice Test main label with submenu for DSAT and ACT */}
                <li style={{ marginBottom: 8, position: 'relative' }}>
                  <div
                    className="flex w-full items-center gap-3 text-[#030303] font-medium whitespace-nowrap text-base font-semibold"
                    style={{
                      padding: '12px 28px',
                      cursor: 'default',
                      color:
                        location.pathname === '/practice_tests' ||
                        location.pathname === '/act_practice_tests'
                          ? '#DE3C7D'
                          : '#030303',
                    }}
                  >
                    {/* Icon for Practice Test (use a suitable icon or keep as T) */}
                    <span className="self-stretch flex w-6 shrink-0 h-6 my-auto">
                      {/* Example: T in a rounded box, replace with icon if needed */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          border: '2px solid #DE3C7D',
                          borderRadius: 8,
                          color: '#DE3C7D',
                          fontWeight: 700,
                          fontSize: 18,
                        }}
                      >
                        T
                      </span>
                    </span>
                    <span
                      className={
                        location.pathname === '/practice_tests' ||
                        location.pathname === '/act_practice_tests'
                          ? 'text-[#DE3C7D] font-bold'
                          : ''
                      }
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: '#DE3C7D',
                      }}
                    >
                      Practice Test
                    </span>
                  </div>
                  {/* Sub-menu for DSAT and ACT */}
                  <div className="relative flex w-full max-w-[172px] flex-col font-medium mt-2.5 ml-8">
                    {/* Gray vertical line for all sub-links */}
                    <div className="bg-[rgba(246,246,246,1)] absolute z-0 flex min-h-full w-0.5 left-2.5 top-0 bottom-0" />
                    {/* DSAT sub-link */}
                    <a
                      href="/practice_tests"
                      className={`relative z-0 flex w-full items-start gap-3 whitespace-nowrap mt-1 px-10 py-2 rounded-lg max-md:px-5 ${
                        location.pathname === '/practice_tests'
                          ? 'text-[#DE3C7D] font-bold'
                          : 'text-[#030303]'
                      }`}
                      style={{ position: 'relative' }}
                    >
                      <div className="z-0 flex-1 shrink basis-[0%] my-auto">
                        DSAT
                      </div>
                      {/* L-connector */}
                      <div
                        className={`absolute z-0 flex w-[13px] h-2 left-2.5 top-2/4 -translate-y-2/4 rounded-bl-lg border-l-2 border-b-2 border-solid ${
                          location.pathname === '/practice_tests'
                            ? 'border-[color:var(--Red-Pink,#DE3C7D)]'
                            : 'border-[rgba(246,246,246,1)]'
                        }`}
                      />
                      {/* Pink vertical line for active sub-link */}
                      {location.pathname === '/practice_tests' && (
                        <div
                          className="bg-[#DE3C7D] absolute z-0 flex w-0.5 left-2.5"
                          style={{ top: 0, height: '100%' }}
                        />
                      )}
                    </a>
                    {/* ACT sub-link */}
                    <a
                      href="/act_practice_tests"
                      className={`relative z-0 flex w-full items-start gap-3 whitespace-nowrap mt-1 px-10 py-2 rounded-lg max-md:px-5 ${
                        location.pathname === '/act_practice_tests'
                          ? 'text-[#DE3C7D] font-bold'
                          : 'text-[#030303]'
                      }`}
                      style={{ position: 'relative' }}
                    >
                      <div className="z-0 flex-1 shrink basis-[0%] my-auto">
                        ACT
                      </div>
                      {/* L-connector */}
                      <div
                        className={`absolute z-0 flex w-[13px] h-2 left-2.5 top-2/4 -translate-y-2/4 rounded-bl-lg border-l-2 border-b-2 border-solid ${
                          location.pathname === '/act_practice_tests'
                            ? 'border-[color:var(--Red-Pink,#DE3C7D)]'
                            : 'border-[rgba(246,246,246,1)]'
                        }`}
                      />
                      {/* Pink vertical line for active sub-link */}
                      {location.pathname === '/act_practice_tests' && (
                        <div
                          className="bg-[#DE3C7D] absolute z-0 flex w-0.5 left-2.5"
                          style={{ top: 0, height: '100%' }}
                        />
                      )}
                    </a>
                  </div>
                </li>
              </>
            )}
            {rolename === 'Admin' &&
              navItems
                .filter((item) => !item.role || item.role === rolename)
                .map(renderMenuItem)}
            {rolename === 'Subadmin' &&
              navItems2
                .filter((item) => !item.role || item.role === rolename)
                .map(renderMenuItem)}
          </ul>
        </nav>
      </aside>
      <style>{`
        .sidebar-modern {
          box-shadow: 4px 0 16px 0 rgba(0,0,0,0.06);
          position: relative;
          overflow: hidden;
        }
        .sidebar-link {
          display: flex;
          align-items: center;
          padding: 12px 28px;
          font-size: 1rem;
          font-weight: 500;
          color: #222;
          border-radius: 12px;
          margin: 4px 12px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }
        .sidebar-link.active {
          background: #FFF0F6;
          color: #E6487C;
          font-weight: 600;
        }
        .sidebar-link:hover {
          background: #f5f5f5;
          color: #E6487C;
        }
        .sidebar-label {
          font-weight: 500;
          letter-spacing: 0.01em;
        }
      `}</style>
    </>
  )
}

export default Leftbar
